import {
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";
import { firestore } from "../firebase";
import { CheckInEventData } from "../types/CheckInEvent";
import { debugLogError } from "../utils/debug-log-error";

const CHECK_IN_EVENTS_COLLECTION = "checkInEvents";
const PLACES_COLLECTION = "places";

/**
 * Checks a user into a place.
 * Handles closing any existing open check-in for the user before creating a new one.
 * Updates the place's checkedInUserIds list.
 * All operations are performed atomically using a batch write.
 *
 * @param userId - The ID of the user checking in.
 * @param placeId - The ID of the place to check into.
 * @param placeName - The name of the place.
 * @returns Promise<void> that resolves when the check-in is complete.
 */
export async function checkIn(
  userId: string,
  placeId: string,
  placeName: string
): Promise<void> {
  const batch = writeBatch(firestore);

  // 1. Find and Close Existing Check-in
  const q = query(
    collection(firestore, CHECK_IN_EVENTS_COLLECTION),
    where("userId", "==", userId),
    where("checkOutTime", "==", null),
    orderBy("checkInTime", "desc"),
    limit(1)
  );

  const existingCheckInsSnapshot = await getDocs(q);

  if (!existingCheckInsSnapshot.empty) {
    const existingCheckInDoc = existingCheckInsSnapshot.docs[0];
    const oldPlaceId = existingCheckInDoc.data().placeId as string;

    // Add update to batch: Set checkOutTime for the existing check-in
    const existingCheckInRef = doc(
      firestore,
      CHECK_IN_EVENTS_COLLECTION,
      existingCheckInDoc.id
    );
    batch.update(existingCheckInRef, { checkOutTime: serverTimestamp() });

    // Add update to batch: Remove userId from old place's checkedInUserIds
    if (oldPlaceId) {
      const oldPlaceRef = doc(firestore, PLACES_COLLECTION, oldPlaceId);
      batch.update(oldPlaceRef, {
        checkedInUserIds: arrayRemove(userId),
      });
    }
  }

  // 2. Create New Check-in Event
  // Create a new document reference with an auto-generated ID
  const newCheckInEventRef = doc(
    collection(firestore, CHECK_IN_EVENTS_COLLECTION)
  );
  batch.set(newCheckInEventRef, {
    userId,
    placeId,
    placeName,
    checkInTime: serverTimestamp(),
    checkOutTime: null,
  });

  // 3. Update Place's Checked-in Users and Check-in History
  const placeRef = doc(firestore, PLACES_COLLECTION, placeId);
  batch.update(placeRef, {
    checkedInUserIds: arrayUnion(userId),
    [`checkInHistory.${userId}`]: serverTimestamp(), // Update specific user's check-in time
  });

  // 4. Commit Batch
  await batch.commit();
}

/**
 * Checks a user out from a place.
 * Updates the check-in event's checkOutTime and removes the user from the place's checkedInUserIds list.
 * All operations are performed atomically using a batch write.
 *
 * @param userId - The ID of the user checking out.
 * @param placeId - The ID of the place from which the user is checking out.
 * @param checkInEventId - The ID of the specific check-in event to close.
 * @returns Promise<void> that resolves when the check-out is complete.
 */
export async function checkOut(
  userId: string,
  placeId: string,
  checkInEventId: string
): Promise<void> {
  const batch = writeBatch(firestore);

  // 1. Update Check-in Event
  const checkInEventRef = doc(
    firestore,
    CHECK_IN_EVENTS_COLLECTION,
    checkInEventId
  );
  batch.update(checkInEventRef, { checkOutTime: serverTimestamp() });

  // 2. Update Place's Checked-in Users
  const placeRef = doc(firestore, PLACES_COLLECTION, placeId);
  batch.update(placeRef, {
    checkedInUserIds: arrayRemove(userId),
  });

  // 3. Commit Batch
  await batch.commit();
}

/**
 * Retrieves the user's current active check-in (where checkOutTime is null).
 *
 * @param userId - The ID of the user whose current check-in is to be fetched.
 * @returns A Promise that resolves to CheckInEventData if an active check-in is found, otherwise null.
 */
export async function getUserCurrentCheckIn(
  userId: string
): Promise<CheckInEventData | null> {
  if (!userId) {
    return null;
  }

  const q = query(
    collection(firestore, CHECK_IN_EVENTS_COLLECTION),
    where("userId", "==", userId),
    where("checkOutTime", "==", null),
    orderBy("checkInTime", "desc"),
    limit(1)
  );

  const querySnapshot = await getDocs(q);

  if (!querySnapshot.empty) {
    const docSnap = querySnapshot.docs[0];
    return {
      id: docSnap.id,
      ...(docSnap.data() as Omit<CheckInEventData, "id">),
    };
  } else {
    return null;
  }
}

/**
 * Fetches the most recent check-in events from Firestore.
 *
 * @param count The number of recent check-ins to fetch.
 * @returns A promise that resolves to an array of CheckInEventData objects.
 */
export const getRecentCheckIns = async (
  count: number
): Promise<CheckInEventData[]> => {
  try {
    const checkInsCollectionRef = collection(
      firestore,
      CHECK_IN_EVENTS_COLLECTION
    );
    const q = query(
      checkInsCollectionRef,
      orderBy("checkInTime", "desc"),
      limit(count)
    );

    const querySnapshot = await getDocs(q);
    const checkIns: CheckInEventData[] = [];
    querySnapshot.forEach((doc) => {
      checkIns.push({ id: doc.id, ...doc.data() } as CheckInEventData);
    });

    return checkIns;
  } catch (error) {
    debugLogError("getRecentCheckIns", error);
    return [];
  }
};

/**
 * Retrieves the check-in history for a specific user.
 *
 * @param userId - The ID of the user whose check-in history is to be fetched.
 * @returns A Promise that resolves to an array of CheckInEventData objects, ordered by checkInTime descending.
 */
export async function getUserCheckInHistory(
  userId: string
): Promise<CheckInEventData[]> {
  if (!userId) {
    return [];
  }

  const q = query(
    collection(firestore, CHECK_IN_EVENTS_COLLECTION),
    where("userId", "==", userId),
    orderBy("checkInTime", "desc")
  );

  const querySnapshot = await getDocs(q);
  const checkInHistory: CheckInEventData[] = [];

  querySnapshot.forEach((doc) => {
    checkInHistory.push({
      id: doc.id,
      ...(doc.data() as Omit<CheckInEventData, "id">),
    });
  });

  return checkInHistory;
}
